import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const ApplicationTable = ({
  applications,
  selectedIds,
  onSelectAll,
  onSelectOne,
  onViewDocument,
  onApprove,
  onReject,
  onViewDetails,
  onStatusChange,
  onSelectApplication,
  selectedApplicationIds,
  onOpenLogger
}) => {
  const [sortField, setSortField] = useState('applicationDate');
  const [sortDirection, setSortDirection] = useState('desc');
  const [changingStatusFor, setChangingStatusFor] = useState(null);

  const getStatusDisplay = (status) => {
    const statusMap = {
      'unbestätigt': { label: 'Unbestätigt', color: 'bg-yellow-100 text-yellow-800 border-yellow-300' },
      'bestätigt': { label: 'Bestätigt', color: 'bg-cyan-100 text-cyan-800 border-cyan-300' },
      'processing': { label: 'Beantragt', color: 'bg-blue-100 text-blue-800 border-blue-300' },
      'approved': { label: 'Genehmigt', color: 'bg-green-100 text-green-800 border-green-300' },
      'rejected': { label: 'Abgelehnt', color: 'bg-red-100 text-red-800 border-red-300' },
      'redeemed': { label: 'Eingelöst', color: 'bg-purple-100 text-purple-800 border-purple-300' }
    };
    return statusMap?.[status] || { label: status, color: 'bg-gray-100 text-gray-800 border-gray-300' };
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedApplications = [...applications]?.sort((a, b) => {
    let aValue = a?.[sortField];
    let bValue = b?.[sortField];
    
    if (sortField === 'applicationDate') {
      aValue = new Date(aValue)?.getTime();
      bValue = new Date(bValue)?.getTime();
    }
    
    if (sortDirection === 'asc') {
      return aValue > bValue ? 1 : -1;
    }
    return aValue < bValue ? 1 : -1;
  });

  const allSelected = applications?.length > 0 && selectedIds?.length === applications?.length;
  const someSelected = selectedIds?.length > 0 && selectedIds?.length < applications?.length;

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse">
        <thead>
          <tr className="bg-muted/50 border-b border-border">
            <th className="p-4 text-left">
              <input
                type="checkbox"
                checked={allSelected}
                ref={(el) => el && (el.indeterminate = someSelected)}
                onChange={onSelectAll}
                className="w-4 h-4 rounded border-input text-primary focus:ring-2 focus:ring-ring cursor-pointer"
                aria-label="Alle Anträge auswählen"
              />
            </th>
            <th className="p-4 text-left sticky left-0 bg-muted/50 z-10 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]">
              <button
                onClick={() => handleSort('customerName')}
                className="flex items-center gap-2 text-sm font-medium text-foreground hover:text-primary transition-smooth"
              >
                Kunde
                <Icon 
                  name={sortField === 'customerName' ? (sortDirection === 'asc' ? 'ChevronUp' : 'ChevronDown') : 'ChevronsUpDown'} 
                  size={16} 
                />
              </button>
            </th>
            <th className="p-4 text-left">
              <span className="text-sm font-medium text-foreground">Geschlecht</span>
            </th>
            <th className="p-4 text-left">
              <button
                onClick={() => handleSort('dealershipName')}
                className="flex items-center gap-2 text-sm font-medium text-foreground hover:text-primary transition-smooth"
              >
                Partner
                <Icon 
                  name={sortField === 'dealershipName' ? (sortDirection === 'asc' ? 'ChevronUp' : 'ChevronDown') : 'ChevronsUpDown'} 
                  size={16} 
                />
              </button>
            </th>
            <th className="p-4 text-left">
              <span className="text-sm font-medium text-foreground">Fahrzeug</span>
            </th>
            <th className="p-4 text-left">
              <span className="text-sm font-medium text-foreground">FIN</span>
            </th>
            <th className="p-4 text-left">
              <span className="text-sm font-medium text-foreground">Kennzeichen</span>
            </th>
            <th className="p-4 text-left">
              <button
                onClick={() => handleSort('applicationDate')}
                className="flex items-center gap-2 text-sm font-medium text-foreground hover:text-primary transition-smooth"
              >
                Antragsdatum
                <Icon 
                  name={sortField === 'applicationDate' ? (sortDirection === 'asc' ? 'ChevronUp' : 'ChevronDown') : 'ChevronsUpDown'} 
                  size={16} 
                />
              </button>
            </th>
            <th className="p-4 text-left">
              <button
                onClick={() => handleSort('status')}
                className="flex items-center gap-2 text-sm font-medium text-foreground hover:text-primary transition-smooth"
              >
                Status
                <Icon 
                  name={sortField === 'status' ? (sortDirection === 'asc' ? 'ChevronUp' : 'ChevronDown') : 'ChevronsUpDown'} 
                  size={16} 
                />
              </button>
            </th>
            <th className="p-4 text-left">
              <span className="text-sm font-medium text-foreground">Gutscheincode</span>
            </th>
            <th className="p-4 text-left">
              <span className="text-sm font-medium text-foreground">Jahreserneuerung</span>
            </th>
            <th className="p-4 text-left">
              <span className="text-sm font-medium text-foreground">Aktionen</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {sortedApplications?.map((application) => (
            <tr 
              key={application?.id} 
              className="border-b border-border hover:bg-muted/30 transition-smooth"
            >
              <td className="p-4">
                <input
                  type="checkbox"
                  checked={selectedIds?.includes(application?.id)}
                  onChange={() => onSelectOne(application?.id)}
                  className="w-4 h-4 rounded border-input text-primary focus:ring-2 focus:ring-ring cursor-pointer"
                  aria-label={`Antrag von ${application?.customerName} auswählen`}
                />
              </td>
              <td className="p-4 sticky left-0 bg-background z-10 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Icon name="User" size={20} color="var(--color-primary)" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground">{application?.customerName}</p>
                    <p className="text-xs text-muted-foreground">{application?.email}</p>
                  </div>
                </div>
              </td>
              <td className="p-4">
                <span className="text-sm text-foreground">
                  {application?.customerGender === 'male' ? 'Herr' :
                   application?.customerGender === 'female' ? 'Frau' :
                   application?.customerGender === 'other' ? 'Divers' :
                   application?.customerGender === 'company' ? 'Firma' : '—'}
                </span>
              </td>
              <td className="p-4">
                <div className="flex items-center gap-2">
                  {application?.dealershipLogo && (
                    <Image
                      src={application?.dealershipLogo}
                      alt={`${application?.dealershipName} Logo zeigt Markenidentität des Partners`}
                      className="w-8 h-8 rounded object-contain"
                    />
                  )}
                  <span className="text-sm text-foreground">{application?.dealershipName}</span>
                </div>
              </td>
              <td className="p-4">
                <p className="text-sm text-foreground">{application?.vehicleModel}</p>
              </td>
              <td className="p-4">
                <code className="text-xs font-mono bg-muted px-2 py-1 rounded text-foreground">
                  {application?.chassisNumber}
                </code>
              </td>
              <td className="p-4">
                <span className="text-sm text-foreground">
                  {application?.customerLicensePlate || '—'}
                </span>
              </td>
              <td className="p-4">
                <p className="text-sm text-foreground">{application?.applicationDate}</p>
              </td>
              <td className="p-4">
                <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${getStatusDisplay(application?.status)?.color}`}>
                  {getStatusDisplay(application?.status)?.label}
                </div>
              </td>
              <td className="p-4">
                {application?.status === 'approved' && application?.voucherCode ? (
                  <code className="text-sm font-mono bg-green-50 text-green-700 px-3 py-1 rounded border border-green-200">
                    {application?.voucherCode}
                  </code>
                ) : (
                  <span className="text-xs text-muted-foreground">—</span>
                )}
              </td>
              <td className="p-4">
                {application?.wantsAnnualRenewal ? (
                  <div className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-blue-50 text-blue-700 border border-blue-200">
                    <Icon name="Mail" size={14} />
                    <span className="text-xs font-medium">Ja</span>
                  </div>
                ) : (
                  <span className="text-xs text-muted-foreground">—</span>
                )}
              </td>
              <td className="p-4">
                <div className="flex items-center gap-2">
                  {/* Status Change Dropdown - Show for bestätigt and processing status */}
                  {(application?.status === 'bestätigt' || application?.status === 'processing') && (
                    <div className="relative">
                      <select
                        value=""
                        onChange={(e) => {
                          const newStatus = e?.target?.value;
                          if (newStatus) {
                            // Validate status transition before allowing change
                            const currentStatus = application?.status;
                            const allowedTransitions = {
                              'unbestätigt': ['processing'],
                              'bestätigt': ['processing'],
                              'processing': ['approved', 'rejected'],
                              'approved': ['redeemed'],
                              'rejected': [],
                              'redeemed': []
                            };
                            
                            const allowed = allowedTransitions?.[currentStatus] || [];
                            
                            if (!allowed?.includes(newStatus)) {
                              alert(`Ungültiger Statusübergang: "${currentStatus}" kann nicht zu "${newStatus}" geändert werden.\n\nErlaubte Übergänge:\n- unbestätigt → beantragt\n- bestätigt → beantragt\n- beantragt → genehmigt/abgelehnt\n- genehmigt → eingelöst`);
                              return;
                            }
                            
                            onStatusChange?.(application?.id, newStatus);
                          }
                        }}
                        className="text-xs border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Status ändern</option>
                        {/* Show only valid transitions based on current status */}
                        {application?.status === 'unbestätigt' && (
                          <option value="processing">🔄 Beantragt</option>
                        )}
                        {application?.status === 'bestätigt' && (
                          <option value="processing">🔄 Beantragt</option>
                        )}
                        {application?.status === 'processing' && (
                          <>
                            <option value="approved">✅ Genehmigt</option>
                            <option value="rejected">❌ Abgelehnt</option>
                          </>
                        )}
                        {application?.status === 'approved' && (
                          <option value="redeemed">🎫 Eingelöst</option>
                        )}
                        {/* Rejected and redeemed are final states - no options */}
                      </select>
                    </div>
                  )}
                  
                  <button
                    onClick={() => onViewDocument(application)}
                    className="p-2 rounded-lg hover:bg-muted transition-smooth"
                    title="Dokument anzeigen"
                    aria-label={`Dokument für ${application?.customerName} anzeigen`}
                  >
                    <Icon name="FileText" size={18} color="var(--color-muted-foreground)" />
                  </button>
                  <button
                    onClick={() => onViewDetails(application)}
                    className="p-2 rounded-lg hover:bg-muted transition-smooth"
                    title="Details anzeigen"
                    aria-label={`Details für ${application?.customerName} anzeigen`}
                  >
                    <Icon name="Eye" size={18} color="var(--color-muted-foreground)" />
                  </button>
                  <button
                    onClick={() => onOpenLogger?.(application)}
                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    title="Database Query Logger"
                  >
                    <Icon name="Bug" size={18} />
                  </button>
                  {application?.status === 'applied' && (
                    <>
                      <button
                        onClick={() => onApprove(application?.id)}
                        className="p-2 rounded-lg hover:bg-success/10 transition-smooth"
                        title="Genehmigen"
                        aria-label={`Antrag von ${application?.customerName} genehmigen`}
                      >
                        <Icon name="CheckCircle2" size={18} color="var(--color-success)" />
                      </button>
                      <button
                        onClick={() => onReject(application?.id)}
                        className="p-2 rounded-lg hover:bg-error/10 transition-smooth"
                        title="Ablehnen"
                        aria-label={`Antrag von ${application?.customerName} ablehnen`}
                      >
                        <Icon name="XCircle" size={18} color="var(--color-error)" />
                      </button>
                    </>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {sortedApplications?.length === 0 && (
        <div className="text-center py-12">
          <Icon name="Inbox" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-4" />
          <p className="text-muted-foreground">Keine Anträge gefunden</p>
        </div>
      )}
    </div>
  );
};

export default ApplicationTable;